import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TIERS = [
  {
    name: 'Seed',
    price: '$19',
    period: '/mo',
    badge: null,
    description: 'Start your investment journey',
    features: [
      'WIN Community Access',
      'Basic Portfolio Tracker',
      '3 Academy Courses/Month',
      'Monthly Newsletter',
      'Member Directory',
      'WIN Marketplace Access',
    ],
    cta: 'Start with Seed',
    style: 'default',
  },
  {
    name: 'Growth',
    price: '$49',
    period: '/mo',
    badge: 'Most Popular',
    description: 'Accelerate your wealth building',
    features: [
      'Everything in Seed',
      'Full Portfolio Tracker + AI',
      'Unlimited Academy Courses',
      'Investment Opportunities',
      'Live Webinar Access',
      'Priority Community Support',
      'WIN AI Assistant',
      'Mentorship Matching',
    ],
    cta: 'Choose Growth',
    style: 'featured',
  },
  {
    name: 'Pinnacle',
    price: '$99',
    period: '/mo',
    badge: 'Elite Access',
    description: 'The ultimate WIN experience',
    features: [
      'Everything in Growth',
      'VIP Investment Deals',
      '1-on-1 Wealth Coaching',
      'Exclusive Pinnacle Events',
      'Early Access to Launches',
      'Gold WIN Coin Keepsake',
      'Dedicated Account Manager',
      'Annual Strategy Session',
    ],
    cta: 'Go Pinnacle',
    style: 'premium',
  },
];

const TierCard = ({ tier, index, visible }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);
  const [btnPressed, setBtnPressed] = useState(false);
  const isFeatured = tier?.style === 'featured';
  const isPremium = tier?.style === 'premium';
  const isSeed = tier?.style === 'default';

  const getCardTransform = () => {
    if (!hovered) return 'translateY(0) scale(1)';
    if (isFeatured) return 'translateY(-8px) scale(1.02)';
    return 'translateY(-6px) scale(1)';
  };

  const getCardShadow = () => {
    if (!hovered) {
      if (isFeatured) return '0 8px 32px rgba(212,165,32,0.2)';
      return '0 2px 12px rgba(0,0,0,0.1)';
    }
    if (isFeatured) return '0 16px 48px rgba(212,165,32,0.4), 0 8px 24px rgba(212,165,32,0.2)';
    if (isPremium) return '0 16px 48px rgba(11,31,59,0.6), 0 8px 24px rgba(212,165,32,0.15)';
    return '0 12px 32px rgba(11,31,59,0.4), 0 4px 16px rgba(11,31,59,0.2)';
  };

  const getBtnTransform = () => {
    if (btnPressed) return 'scale(0.97)';
    if (btnHovered) return 'scale(1.03)';
    return 'scale(1)';
  };

  return (
    <div
      className={`relative rounded-2xl p-5 sm:p-8 cursor-pointer ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${
        isFeatured
          ? 'bg-white border-2 border-[#D4A520]'
          : isPremium
          ? 'bg-gradient-to-br from-[#1a3060] to-[#0B1F3B] border border-[#D4A520]/30'
          : 'bg-[#0B1F3B]/60 border border-white/10'
      }`}
      style={{
        transitionDelay: `${index * 150}ms`,
        transition: 'opacity 700ms ease, transform 300ms ease-out, box-shadow 300ms ease-out',
        transform: visible ? getCardTransform() : 'translateY(32px)',
        boxShadow: getCardShadow(),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Shimmer border for featured */}
      {isFeatured && (
        <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4A520]/20 to-transparent animate-shimmer" />
        </div>
      )}
      {/* Badge */}
      {tier?.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold tracking-wide ${
          isFeatured ? 'bg-[#D4A520] text-[#0B1F3B]' : 'bg-[#D4A520]/20 text-[#D4A520] border border-[#D4A520]/40'
        }`}>
          {isPremium && (
            <span
              style={{
                display: 'inline-block',
                transition: 'transform 300ms ease-out',
                transform: hovered ? 'scale(1.1)' : 'scale(1)',
              }}
            >👑 </span>
          )}{tier?.badge}
        </div>
      )}
      <div className="mb-6">
        <h3 className={`text-2xl font-bold mb-1 ${isFeatured ? 'text-[#0B1F3B]' : 'text-white'}`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {tier?.name}
        </h3>
        <p className={`text-sm mb-4 ${isFeatured ? 'text-[#0B1F3B]/60' : 'text-white'}`}>{tier?.description}</p>
        <div className="flex items-baseline gap-1">
          <span className={`text-5xl font-bold ${isFeatured ? 'text-[#0B1F3B]' : 'text-[#D4A520]'}`} style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {tier?.price}
          </span>
          <span className={`text-sm ${isFeatured ? 'text-[#0B1F3B]/50' : 'text-white'}`}>{tier?.period}</span>
        </div>
      </div>
      <ul className="space-y-3 mt-6 mb-6">
        {tier?.features?.map((f) => (
          <li key={f} className="flex items-center gap-3">
            <span className="text-[#D4A520] text-sm">✓</span>
            <span className={`text-sm ${isFeatured ? 'text-[#0B1F3B]/80' : 'text-white'}`}>{f}</span>
          </li>
        ))}
      </ul>
      {/* CTA button with shine sweep */}
      <div className="relative overflow-hidden rounded-xl">
        <button
          onClick={() => navigate('/onboarding-authentication')}
          onMouseEnter={() => setBtnHovered(true)}
          onMouseLeave={() => { setBtnHovered(false); setBtnPressed(false); }}
          onMouseDown={() => setBtnPressed(true)}
          onMouseUp={() => setBtnPressed(false)}
          className={`relative w-full py-3 rounded-xl font-bold text-sm overflow-hidden ${
            isFeatured
              ? 'bg-[#D4A520] text-[#0B1F3B]'
              : 'border border-[#D4A520] text-[#D4A520] bg-transparent'
          }`}
          style={{
            transition: 'transform 250ms ease-out, box-shadow 250ms ease-out, background 250ms ease-out',
            transform: getBtnTransform(),
            boxShadow: btnHovered && isFeatured ? '0 6px 20px rgba(212,165,32,0.4)' : 'none',
            cursor: 'pointer',
          }}
        >
          {/* Shine sweep overlay */}
          <span
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)',
              transform: btnHovered ? 'translateX(100%)' : 'translateX(-100%)',
              transition: btnHovered ? 'transform 500ms ease' : 'none',
            }}
          />
          {tier?.cta}
        </button>
      </div>
    </div>
  );
};

const MembershipSection = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="membership" className="py-14 bg-[#0B1F3B]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 bg-[#D4A520]/15 border border-[#D4A520]/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-[#D4A520] text-xs font-semibold tracking-widest uppercase">Membership</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#D4A520] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Choose Your Membership
          </h2>
          <p className="text-white text-lg max-w-xl mx-auto">
            Every tier unlocks powerful tools to accelerate your wealth journey.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {TIERS?.map((tier, i) => (
            <TierCard key={tier?.name} tier={tier} index={i} visible={visible} />
          ))}
        </div>

        {/* Founding Member Banner */}
        <div className={`bg-gradient-to-r from-[#D4A520]/20 to-[#D4A520]/10 border border-[#D4A520]/30 rounded-2xl p-6 md:p-8 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <img
              src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
              alt="WIN gold coin keepsake"
              className="w-16 h-16 rounded-full object-cover shadow-lg shadow-[#D4A520]/30"
            />
            <div className="flex-1 text-center md:text-left">
              <h4 className="text-[#D4A520] font-bold text-xl mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Founding Member Offer
              </h4>
              <p className="text-white text-sm">
                First 500 members receive a physical gold WIN coin keepsake — <span className="text-[#D4A520] font-semibold">247 spots remaining</span>
              </p>
            </div>
            <div className="w-full md:w-64">
              <div className="flex justify-between text-xs text-white mb-1.5">
                <span>253 claimed</span>
                <span>500 total</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#D4A520] to-[#F0C830] rounded-full" style={{ width: '50.6%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer { animation: shimmer 3s ease-in-out infinite; }
      `}</style>
    </section>
  );
};

export default MembershipSection;
